/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventModalContent.tsx
import { useFormikContext } from 'formik'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type {
  EventFormValues,
  SnEvent,
} from '@sportninja/common/features/event/types'
import BaseModal from 'src/components/ts-components/BaseModal'
import EventFormFields from './EventFormFields'
import { isSeriesEvent } from '@sportninja/common/features/event/seriesScope'
import css from '@emotion/css/macro'
import BetaBadge from 'src/components/ts-components/BetaBadge'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'

interface EventModalContentProps {
  isOpen: boolean
  onClose: () => void
  event: SnEvent | null
  /** Competition scope — enables the Location/Facility pickers. */
  scheduleId?: string
}

/**
 * The modal shell. Lives inside the Formik provider so it can drive submission
 * from the footer button. Title and OK label switch on add vs edit mode.
 */
const EventModalContent: React.FC<EventModalContentProps> = ({
  isOpen,
  onClose,
  event,
  scheduleId,
}) => {
  const { submitForm, isSubmitting } = useFormikContext<EventFormValues>()
  const isEdit = Boolean(event)

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      // A NODE title bypasses BaseModal's own `<Text variant='heading3'>`
      // wrapper (it only applies that to a string), so the heading style is
      // reproduced here — without it the header loses its type scale.
      title={
        <span css={titleRowStyle}>
          <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
            {capitalizeWords(isEdit ? 'Edit Event' : 'Add Event')}
          </Text>
          <BetaBadge />
        </span>
      }
      okText={isEdit ? 'Save' : 'Create'}
      onOk={submitForm}
      isLoading={isSubmitting}
      // BaseModal defaults to 40vw — only ~512px on a 1280px screen, too
      // cramped for this form's paired rows. A fixed px width keeps the
      // paired fields side-by-side; antd caps it to the viewport on smaller
      // screens, and below MOBILE_BREAKPOINT BaseModal switches to 100%.
      //
      // 900 rather than the previous 760: the start row now carries FOUR
      // controls (date, time, timezone, length) and the timezone labels are
      // long IANA names, so at 760 they truncated to uselessness.
      desktopWidth='900px'
    >
      <EventFormFields
        scheduleId={scheduleId}
        // No `accountTimezone` prop: the zone now lives in the FORM, seeded by
        // `EventFormProvider`'s `defaultTimezone` and thereafter owned by the
        // Timezone select. Passing the account zone here as well would give the
        // date fields a second, stale source.
        //
        // Only an EDIT of a recurring event offers the scope choice — a new
        // event has no series to apply changes across.
        isSeries={isEdit && isSeriesEvent(event)}
      />
    </BaseModal>
  )
}

export default EventModalContent

// ---- styled ----
const titleRowStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`
